<template>
  <div ref="tabsRef" class="ui-pivot-table__tabs">
    <draggable
      :model-value="configsList"
      v-bind="DRAG_OPTIONS"
      :disabled="configsList.length === 1"
      handle=".tabs-item__handle"
      class="ui-pivot-table__tabs__area"
      @remove="handleRemoveElement"
      @sort="handleSort"
    >
      <template #item="{ element, index }">
        <ConfigsTabsItem
          :label="element?.name"
          :active="index === configIndex"
          :is-last-one="configsList.length === 1"
          :edit-on-render="index === addedIndex"
          @duplicate="handleDuplicate(index, element)"
          @remove="handleRemove(index)"
          @update="handleUpdate(index, $event)"
          @click="handleClick(index)"
          @pin="handlePin(index)"
        />
      </template>
    </draggable>
    <div class="ui-pivot-table__tabs__plus" @click="handleCreate">
      <PlusIcon class="ui-pivot-table__tabs__plus__icon" aria-hidden="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'

import { PivotTableConfig, PivotTableStorage } from '../utils/types'

import { DRAG_OPTIONS } from './utils/const'

import { PlusIcon } from '@heroicons/vue/24/outline'
import ConfigsTabsItem from './ConfigsTabsItem.vue'

type Props = {
  configsList: PivotTableConfig[]
  configIndex: number
  defaultConfig: PivotTableConfig
}

type Emits = {
  'update:storage': [data: PivotTableStorage]
  'pin': [data: PivotTableConfig, callback: () => void, index?: number]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const addedIndex = ref<number>()

const tabsRef = useTemplateRef('tabsRef')

const handleSort = (event: any) => {
  const configs = cloneDeep(props.configsList)
  if (!configs) return
  const element = configs[event.oldIndex]
  configs.splice(event.oldIndex, 1)
  configs.splice(event.newIndex, 0, element)
  let index = props.configIndex
  if (index === event.oldIndex) {
    index = event.newIndex
  } else if (event.oldIndex < index && event.newIndex >= index) {
    index--
  } else if (event.oldIndex > index && event.newIndex <= index) {
    index++
  }
  emit('update:storage', { configs, index })
}

const handleCreate = () => {
  const newConfig = cloneDeep(props.defaultConfig)
  const index = props.configsList.length
  addedIndex.value = index
  emit('update:storage', {
    configs: [...props.configsList, newConfig],
    index,
  })
}

const handleDuplicate = (itemIndex: number, data: PivotTableConfig) => {
  const newConfig = cloneDeep(data)
  const configs = cloneDeep(props.configsList)
  configs.splice(itemIndex, 0, newConfig)
  const index = itemIndex + 1
  addedIndex.value = index
  emit('update:storage', { configs, index })
}

const handleRemove = (itemIndex: number) => {
  const configs = cloneDeep(props.configsList)
  configs.splice(itemIndex, 1)
  let index = props.configIndex
  if (itemIndex === props.configIndex) {
    index = 0
  } else if (itemIndex < props.configIndex) {
    index--
  }
  emit('update:storage', { configs, index })
}

const handlePin = (itemIndex: number, newIndex?: number) => {
  const configs = cloneDeep(props.configsList[itemIndex])
  emit('pin', configs, () => {
    handleRemove(itemIndex)
  }, newIndex)
}

const handleUpdate = (itemIndex: number, name: string) => {
  const configs = props.configsList
  configs[itemIndex].name = name
  emit('update:storage', {
    configs,
    index: props.configIndex,
  })
}

const handleClick = (index: number) => {
  emit('update:storage', {
    configs: props.configsList,
    index,
  })
}

const scrollToActiveTab = async (delay = 500) => {
  await nextTick()
  setTimeout(() => {
    const container = tabsRef.value
    const activeTab = container?.querySelector('.tabs-item--active') as HTMLElement
    if (!container || !activeTab) return

    const containerRect = container.getBoundingClientRect()
    const activeTabRect = activeTab.getBoundingClientRect()

    const isFullyVisible =
    activeTabRect.left >= containerRect.left &&
    activeTabRect.right <= containerRect.right

    if (!isFullyVisible) {
      const containerWidth = containerRect.width
      const activeTabWidth = activeTabRect.width
      const activeTabLeft = activeTab.offsetLeft

      const targetScrollLeft = activeTabLeft - (containerWidth / 2) + (activeTabWidth / 2)

      const maxScrollLeft = container.scrollWidth - containerWidth
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft))

      container.scrollTo({
        left: finalScrollLeft,
        behavior: 'smooth',
      })
    }
  }, delay)
}

const handleRemoveElement = (event: any) => {
  handlePin(event.oldIndex, event.newIndex)
}

onMounted(scrollToActiveTab)

watch(addedIndex, async value => {
  if (value === undefined) return
  await nextTick()
  addedIndex.value = undefined
})

watch(
  () => props.configIndex,
  () => {
    scrollToActiveTab(0)
  },
)
</script>

<style>
.ui-pivot-table__tabs {
  @apply w-full;
  @apply relative;
  @apply flex shrink-0;
  @apply pt-1;
  @apply overflow-x-auto;

  &__area {
    @apply flex;
    @apply gap-1;
  }

  &__plus {
    @apply w-8 h-full;
    @apply shrink-0;
    @apply sticky right-0;
    @apply flex items-center justify-center;
    @apply text-gray-950 dark:text-gray-50;
    @apply bg-gray-100 hover:bg-gray-200;
    @apply dark:bg-gray-850 dark:hover:bg-gray-900;
    @apply border-l-4 border-white dark:border-gray-800;
    @apply cursor-pointer;

    &__icon {
      @apply w-5 h-5;
    }
  }
}
</style>
